
/* ══ v14 (W4a): THE APP-SIDE STATE THE REV MACHINERY RUNS ON ═══════════════════════════════
   Deliberately OUTSIDE the extracted block below, which stays pure so qa/merge_props.js can
   eval it under Node with no window, no fetch and no IndexedDB.

   `_revBaseline` is what I last PUBLISHED — the picture every save diffs against. It must be
   re-captured at load, after every successful save, and (from W4b) after every re-merge; a
   stale one silently compares against the wrong portfolio and either bumps nothing or bumps
   everything. `_revTombs` carries the tombstones out of the live arrays and back into the
   bytes — see the pair in the extracted block for why they must not live in either place
   alone. */
let _revBaseline = null;
let _revTombs = [];

/* Load-side counterpart of the save wiring: strip tombstones out of what was just adopted, then
   take the baseline from the clean result. One function, because doing these in the wrong order
   would put tombstones in the baseline and make every one of them look like a fresh deletion on
   the next save. */
function _revAdoptBaseline(db) {
  if (!db || typeof db !== 'object' || !Array.isArray(db.projects)) { _revTombs = []; _revBaseline = null; return; }
  _revTombs = _revHarvestTombstones(db);
  _revBaseline = _revCaptureBaseline(db);
}

/* ── The four per-project id counters, in ONE place ────────────────────────────────────────
   v14 (W4a): extracted from setActiveProject, which is still its main caller. It is a function
   rather than four inline lines because boot has to run exactly the same seeding again once
   the session is known, and a second copy of it would be free to drift.
   ⚠ It may call _mintNextId only because every caller runs AFTER module evaluation:
   MINT_BLOCK_SIZE is a `const`, so a module-level call would hit its TDZ during boot (§3's
   hoisting rule). That is also why the module-level stakeholder initialiser is still on the old
   counter — this re-seed is what governs it in practice. */
function _reseedMintCounters() {
  /* ⚠ v16.8 (W5): the stored marks are honoured here, which is what makes them mean anything.
     This function runs on EVERY project switch and again when the launcher answers, so it is
     the exact moment a freed id used to come back. */
  nextId        = _mintNextId([...ACTIONS, ...newActions], _myWriterId(), appMeta.actSeq);
  nextSessionId = _mintNextId(SESSIONS, _myWriterId(), appMeta.mtgSeq);
  /* ── v10.3 (W4): stakeholder ids come from a PERSISTED high-water mark ───────────────
     They used to be max(remaining)+1, so deleting the highest-id stakeholder handed that id
     straight to the next one added — and because RACI assignments and comms audiences
     reference stakeholder ids BY VALUE (and render inertly when unknown), the newcomer
     silently inherited the deleted person's Accountable row and audience memberships.
     This is the crSeq/riskSeq/asmSeq pattern: lazily created, self-healing if the stored value
     ever lags the data, and never reused once the highest is deleted.
     NOTE: this partially reverses the v8.2 audit's Q3-id "leave" verdict, which rested on "id
     reuse is inert — nothing references these ids". True for actions/sessions/workstreams;
     demonstrably FALSE for stakeholders. See SCOPE §v10.3 W4.2.
     v14 (W3b): the stored mark is honoured only INSIDE my own block — see _mintNextId. */
  nextStakeholderId = _mintNextId(STAKEHOLDERS, _myWriterId(), appMeta.stkSeq);
  nextWorkstreamId  = _mintNextId(WORKSTREAMS, _myWriterId(), appMeta.wsSeq);   /* v16.8 (W5) */
}

/* ── ⚠ v16.8 (W2): ONE definition of "the user has work in progress", because there are now
   TWO places that must ask — before the merge is fetched and again before it is adopted. The
   condition was previously written out inline at one site; a second inline copy is how two
   guards that must agree stop agreeing, which is the drift `actField` was extracted to end.
   ⚠ A half-typed update note counts, exactly as a dirty document does (v14.1 a). */
function _workInProgressBlocksAdoption() {
  return (typeof isDirty !== 'undefined' && isDirty) || _hasPendingUpdateDraft()
    || _projectProgressDialogOpen();
}
